import React, { useEffect, useState } from "react";
import { Paper, Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  PieChart as PieChartRecharts,
  Pie,
  Legend,
  Tooltip,
  Cell,
} from "recharts";
import { getColor } from "../tools";
const OPACITY_VALUE = 0.3;
/* 
const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
}) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  if (percent < 0.1) return null;
  return (
    <text
      fontSize={14}
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};
 */
const CustomLegend = ({
  payload,
  handleMouseEnter,
  handleMouseLeave,
  opacity,
  height,
  labelId,
}) => {
  const getOpacity = (index) => {
    const labelHover = opacity.filter((value) => value !== 1).length === 1;
    if (labelHover && opacity[index] === 1) return 1; // mouse hovers over the labels and the label of interest: show the label of interest
    if (labelHover && opacity[index] !== 1) return OPACITY_VALUE; // mouse hovers over the labels but not over the label of interest: hide the label
    return 1; // mouse don't hover over the labels so show the labels (default)
  };
  return (
    <Box>
      {payload?.map((point, index) => (
        <Box
          key={index}
          item
          display="flex"
          alignItems={"center"}
          sx={{
            gap: 1,
            opacity: getOpacity(index),
          }}
          onMouseEnter={() => handleMouseEnter(index)}
          onMouseLeave={() => handleMouseLeave(index)}
        >
          <div>
            <Box
              width={"14px"}
              height={"14px"}
              borderRadius="50%"
              backgroundColor={
                point?.[`${labelId}$color`] ?? getColor(point.name)
              }
            />
          </div>
          <Typography variant="body2" color="text.secondary">
            {point.name}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/* const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    console.log(payload);
    return (
      <Card sx={{ padding: 2 }}>
        <Typography variant="body2">{`${payload[0].name} : ${payload[0].value}%`}</Typography>
      </Card>
    );
  }

  return null;
}; */
function CustomTooltip({
  active,
  payload,
  labelIds,
  handleMouseEnter,
  handleMouseLeave,
  isAbsolute,
}) {
  const { t } = useTranslation();
  const payloadArr = payload?.[0]?.payload;
  const name = payloadArr?.["name"];
  if (active && payload && payload.length)
    return (
      <Paper style={{ padding: "16px", zIndex: 3 }} elevation={15}>
        <Typography variant="subtitle2">{name}</Typography>
        {labelIds.map(({ id, label }) => {
          const labelValue = payloadArr?.[id];
          const measurements = payloadArr?.[`${id}$numberOfMeasurements`];
          const tooFewMeasurements =
            measurements <= 5 ? `(${t("moodboards.tooFewMeasurements")})` : "";
          const color = payloadArr?.[`${id}$color`] ?? getColor(id);

          const getNormalText = () => {
            if (!isAbsolute) {
              return `${measurements}x ${label}: ${labelValue}%`;
            }
            return `${labelValue}`;
          };

          const getItalicText = () => {
            if (!isAbsolute) {
              return `${tooFewMeasurements}`;
            }
            return "";
          };

          if (labelValue !== undefined && measurements !== undefined)
            return (
              <div key={`${id}Tooltip`}>
                <div style={{ display: "flex" }}>
                  <div
                    style={{
                      height: "12px",
                      width: "12px",
                      backgroundColor: color,
                      marginRight: "8px",
                      marginTop: "3px",
                      borderRadius: "50%",
                      display: "flex",
                    }}
                  ></div>
                  <Typography variant="body2" style={{ marginRight: 3 }}>
                    {getNormalText()}
                  </Typography>
                </div>
                <Typography variant="body2" style={{ fontStyle: "italic" }}>
                  {getItalicText()}
                </Typography>
              </div>
            );
          return <div key={`${id}Tooltip`}></div>;
        })}
      </Paper>
    );
  return null;
}

function CustomLabel(props) {
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    value,
    index,
    name,
    isAbsolute,
    showValues,
  } = props;
  const RADIAN = Math.PI / 180;
  // eslint-disable-next-line
  const radius = 25 + innerRadius + (outerRadius - innerRadius);
  // eslint-disable-next-line
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  // eslint-disable-next-line
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  const getLabelText = () => {
    if (!isAbsolute) {
      return `${value ?? 0}%`;
    }
    return `${value ?? 0}`;
  };

  if (!showValues) return null;
  return (
    <text
      x={x}
      y={y}
      fill="#000"
      //opacity={opacity[index]}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={13}
    >
      {getLabelText()}
    </text>
  );
}

export default function PieChart(props) {
  const {
    data: d,
    quantitativeLabelsChecked,
    height,
    thumbnail = false,
    showValues,
    sortDec,
    isAbsolute = false,
  } = props;
  const [data, setData] = useState(d);
  const [opacity, setOpacity] = useState(new Array(data.length).fill(1));

  const handleMouseEnter = (index) => {
    const newOpacity = opacity.map((_, i) => (index === i ? 1 : OPACITY_VALUE));
    setOpacity(newOpacity);
  };

  const handleMouseLeave = () => {
    setOpacity(new Array(data.length).fill(1));
  };

  useEffect(() => {
    if (sortDec) {
      const id = quantitativeLabelsChecked?.[0]?.id;
      const sortedData = d.sort((a, b) => b[id] - a[id]);
      setData(sortedData);
    } else {
      setData(d);
    }
  }, [quantitativeLabelsChecked, d, sortDec]);

  return (
    <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
      <PieChartRecharts
        margin={{ left: 0, top: 40, bottom: 40 }}
        al
        width={300}
        height={height}
      >
        <Pie
          dataKey={quantitativeLabelsChecked?.[0]?.id}
          isAnimationActive={false}
          data={data}
          cx="50%"
          cy="50%"
          outerRadius={80}
          innerRadius={0}
          labelLine={showValues ? { stroke: "#000" } : false}
          startAngle={-270}
          label={
            <CustomLabel showValues={showValues} isAbsolute={isAbsolute} />
          }
        >
          {data.map((el, index) => {
            return (
              <Cell
                key={index}
                fill={
                  el?.[`${quantitativeLabelsChecked?.[0]?.id}$color`] ??
                  getColor(el.name)
                }
                opacity={opacity[index]}
              />
            );
          })}
        </Pie>
        <Tooltip
          content={
            <CustomTooltip
              labelIds={[quantitativeLabelsChecked[0]]}
              isAbsolute={isAbsolute}
              handleMouseEnter={handleMouseEnter}
              handleMouseLeave={handleMouseLeave}
            />
          }
        />
        {/*  <Legend
        verticalAlign="bottom"
        content={
          <CustomLegend
            handleMouseEnter={handleMouseEnter}
            handleMouseLeave={handleMouseLeave}
            opacity={opacity}
          />
        }
      /> */}
      </PieChartRecharts>
      <CustomLegend
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
        opacity={opacity}
        payload={data}
        height={height}
        labelId={quantitativeLabelsChecked[0].id}
      />
    </Box>
  );
}
